import PropTypes from 'prop-types';
import { createRef, isValidElement, Component } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import {
  isFinite,
  isArray,
  isNil,
  reduce,
  noop,
  isEmpty,
  isBoolean,
  isString,
  map,
} from 'lodash';

import { Icon } from 'components/elements/icon';
import { Tooltip } from 'components/overlay/Tooltip';
import { ValidationErrorIndicator } from 'components/form/validationErrorIndicator';
import { toFormattedMessage } from 'components/utils/text';
import { TagEl } from 'components/navigation/menu/link';
import { LinkImage } from 'components/navigation/menu/styles/link';

import * as styled from './styles/select';
import i18n from './utils/i18n';

import { isValid } from '../../utils/form-utils';

class SelectComponent extends Component {
  static getDerivedStateFromProps(props, state) {
    if (props.options !== state.options) {
      return { options: props.options };
    }

    return null;
  }

  constructor(props) {
    super(props);
    this.selectionNameRef = createRef();
    this.state = {
      focused: false,
      options: props.options,
      optionsPosition: props.optionsPosition,
      selectionTruncated: false,
    };
  }

  shouldComponentUpdate(nextProps, nextState) {
    const nextVals = this.getPropertiesToCompareFor(nextProps, nextState);
    const oldVals = this.getPropertiesToCompareFor(this.props, this.state);

    return reduce(
      nextVals,
      (updateComponent, val, key) =>
        updateComponent || nextVals[key] !== oldVals[key],
      false
    );
  }

  handleFocus(focused) {
    const { onFocus } = this.props;
    const onFocusCallback = onFocus || noop;

    this.setState({ focused }, onFocusCallback);
  }

  handleOptionsPosition = (optionsPosition) => {
    this.setState({ optionsPosition });
  };

  handleSelectionMouseEnter = () => {
    const name = this.selectionNameRef.current;

    this.setState({
      selectionTruncated: Boolean(name) && name.scrollWidth > name.clientWidth,
    });
  };

  getPropertiesToCompareFor(propsToExtract, stateToExtract) {
    const { selected, disabled, shouldValidate } = propsToExtract;
    const {
      focused,
      options,
      optionsPosition,
      selectionTruncated,
    } = stateToExtract;

    return {
      selected,
      options,
      disabled,
      focused,
      shouldValidate,
      optionsPosition,
      selectionTruncated,
    };
  }

  getRenderedContent = (selectedOption, optionName) => {
    if (selectedOption && selectedOption.image) {
      return (
        <LinkImage src={selectedOption.image} srcSet={selectedOption.srcset} />
      );
    }

    if (isValidElement(optionName)) {
      return optionName;
    }

    return (
      <styled.OptionName ref={this.selectionNameRef}>
        {optionName}
      </styled.OptionName>
    );
  };

  selectionLabel(optionName, tags) {
    const { intl } = this.props;
    const tagTexts = map(tags, ({ text }) =>
      isString(text) ? text : intl.formatMessage(text)
    );

    return [optionName, ...tagTexts].join(' · ');
  }

  updateValue(selected) {
    const { onChange } = this.props;
    onChange(selected);
  }

  equalSlugs(first, second) {
    const firstSlug = isFinite(first) ? first.toString() : first;
    const secondSlug = isFinite(second) ? second.toString() : second;
    return firstSlug === secondSlug;
  }

  optionsWithoutSelected() {
    const { selected } = this.props;
    const { options } = this.state;
    return options.filter((option) => !this.equalSlugs(selected, option.slug));
  }

  renderSelection(selectedOption, displayedSelection) {
    const { intl, selected, selectionSource, selectionTooltip } = this.props;
    const { selectionTruncated } = this.state;
    const { linkName, selectedName, values } = displayedSelection;
    const optionName = toFormattedMessage(
      selectedName || linkName,
      intl,
      values
    );

    const selectionArchived =
      !isEmpty(selected) && selected !== 'none' && !selectedOption;

    if (selectionArchived && selectionSource === 'bullhorn') {
      return (
        <styled.ArchivedSelection>
          <Tooltip
            size="23rem"
            fixed
            content={<FormattedMessage {...i18n.bullhornOptionTooltip} />}
          >
            <styled.NarrowVerticalboxIcon>
              <Icon icon="Bullhorn" color="bhOrange" size="larger" />
            </styled.NarrowVerticalboxIcon>
          </Tooltip>
          <styled.OptionName>{selected}</styled.OptionName>
        </styled.ArchivedSelection>
      );
    }

    if (selectionArchived && selectionSource) {
      return (
        <styled.ArchivedSelection>
          <Tooltip
            size="23rem"
            fixed
            content={<FormattedMessage {...i18n.archivedOptionTooltip} />}
          >
            <styled.NarrowVerticalboxIcon>
              <Icon icon="WarningCircle" color="red" size="larger" />
            </styled.NarrowVerticalboxIcon>
          </Tooltip>
          <styled.OptionName>{selected}</styled.OptionName>
        </styled.ArchivedSelection>
      );
    }

    const renderedContent = this.getRenderedContent(selectedOption, optionName);
    const selection = (
      <>
        {renderedContent}
        {isArray(displayedSelection.tags) &&
          displayedSelection.tags.map((tag) => TagEl(tag, true, intl))}
      </>
    );

    return (
      <styled.Selected
        onMouseEnter={selectionTooltip ? this.handleSelectionMouseEnter : noop}
      >
        {displayedSelection.icon && (
          <styled.NarrowVerticalboxIcon>
            <Icon
              icon={displayedSelection.icon}
              size={displayedSelection.iconSize || 'larger'}
            />
          </styled.NarrowVerticalboxIcon>
        )}

        {selectionTooltip ? (
          <styled.SelectionTooltip
            fixed
            disabled={!selectionTruncated}
            content={this.selectionLabel(optionName, displayedSelection.tags)}
          >
            {selection}
          </styled.SelectionTooltip>
        ) : (
          selection
        )}
      </styled.Selected>
    );
  }

  render() {
    const {
      className,
      inputType,
      required,
      shouldValidate = false,
      selected,
      disabled,
      listWithoutSelected = false,
      listWithoutPlaceholder = false,
      withClear,
      selectTag,
      size,
      placeholder,
      optionsHeight,
      optionsFixed,
      wrapperRef,
      optionsPosition: defaultOptionsPosition,
      optionsTypeahead,
      optionsCount,
      dataManual,
      dropdownSize,
      keepOpenOnSelect,
      validationErrorIndicator = true,
    } = this.props;
    const { options, focused: stateFocus, optionsPosition } = this.state;

    const selectedOption =
      isNil(selected) && !listWithoutPlaceholder
        ? options[0]
        : options.filter((option) => this.equalSlugs(selected, option.slug))[0];
    const displayedSelection = selectedOption ||
      (!listWithoutPlaceholder && options[0]) ||
      (!isEmpty(placeholder) && placeholder) || { linkName: '', slug: 'none' };
    const { slug } = displayedSelection;

    const displayedOptions = listWithoutSelected
      ? this.optionsWithoutSelected()
      : options;

    const isInputValid = isValid({
      inputValue: slug,
      shouldValidate,
      inputType: 'option',
      required,
    });

    const displayClear = isBoolean(withClear)
      ? withClear
      : selectedOption &&
        selectedOption.slug !== 'none' &&
        !isNil(selectedOption.slug) &&
        !isEmpty(placeholder);

    return (
      <styled.Select
        className={className}
        inputType={inputType}
        isValid={isInputValid}
        options={displayedOptions}
        disabled={disabled}
        onSelect={(newSelected) => this.updateValue(newSelected)}
        onFocus={(focused) => this.handleFocus(focused)}
        selectTag={selectTag}
        focused={stateFocus}
        size={size}
        optionsFixed={optionsFixed}
        wrapperRef={wrapperRef}
        optionsHeight={optionsHeight}
        listWithoutPlaceholder={listWithoutPlaceholder}
        typeaheadPlaceholder={optionsTypeahead}
        optionsCount={optionsCount}
        optionsPosition={optionsPosition}
        dropdownSize={dropdownSize}
        defaultOptionsPosition={defaultOptionsPosition}
        onOptionsPosition={this.handleOptionsPosition}
        displayClear={displayClear}
        dataManual={dataManual}
        active={selected}
        keepOpenOnSelect={keepOpenOnSelect}
      >
        {shouldValidate && !isInputValid && validationErrorIndicator && (
          <ValidationErrorIndicator />
        )}

        {this.renderSelection(selectedOption, displayedSelection)}
      </styled.Select>
    );
  }
}

SelectComponent.propTypes = {
  intl: PropTypes.object,
  className: PropTypes.string,
  inputType: PropTypes.oneOf(['option', 'currency']),
  selected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectionSource: PropTypes.string,
  selectionTooltip: PropTypes.bool,
  options: PropTypes.array,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  shouldValidate: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  listWithoutSelected: PropTypes.bool,
  listWithoutPlaceholder: PropTypes.bool,
  withClear: PropTypes.bool,
  selectTag: PropTypes.string,
  size: PropTypes.oneOf(['small', 'default']),
  optionsHeight: PropTypes.number,
  optionsFixed: PropTypes.bool,
  wrapperRef: PropTypes.node,
  optionsPosition: PropTypes.oneOf(['top', 'bottom']),
  optionsTypeahead: PropTypes.object,
  optionsCount: PropTypes.object,
  placeholder: PropTypes.object,
  onFocus: PropTypes.func,
  dataManual: PropTypes.string,
  keepOpenOnSelect: PropTypes.bool,
  validationErrorIndicator: PropTypes.bool,
};

SelectComponent.defaultProps = {
  options: [],
  selectTag: 'link',
  size: 'default',
  optionsPosition: 'bottom',
  optionsFixed: false,
  listWithoutPlaceholder: false,
};

export const Select = injectIntl(SelectComponent);
